import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode
} from 'react';
import { Offcanvas } from 'react-bootstrap';
import TaskDetailsContent from 'pages/apps/tasks/components/TaskDetailsContent';
import './taskDetailsOffcanvas.css';

interface TaskDetailsContextValue {
  openTaskId: string | null;
  openTask: (taskId: string) => void;
  closeTask: () => void;
}

const TaskDetailsContext = createContext<TaskDetailsContextValue | undefined>(
  undefined
);

/**
 * App-wide task details panel. Any component can call
 * `useTaskDetails().openTask(id)` to slide in a right-side offcanvas with the
 * full task details — so users stay in context (job card, tasks list, etc.)
 * instead of navigating away. The standalone /tasks/view/:id route still
 * exists for deep links.
 */
export const TaskDetailsProvider = ({ children }: { children: ReactNode }) => {
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);

  const openTask = useCallback((taskId: string) => {
    if (taskId) setOpenTaskId(taskId);
  }, []);

  const closeTask = useCallback(() => setOpenTaskId(null), []);

  const value = useMemo(
    () => ({ openTaskId, openTask, closeTask }),
    [openTaskId, openTask, closeTask]
  );

  return (
    <TaskDetailsContext.Provider value={value}>
      {children}
      <Offcanvas
        show={!!openTaskId}
        onHide={closeTask}
        placement="end"
        className="task-details-offcanvas"
        backdropClassName="task-details-offcanvas-backdrop"
        aria-labelledby="task-details-offcanvas-title"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title id="task-details-offcanvas-title">
            Task Details
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {openTaskId && (
            <TaskDetailsContent taskId={openTaskId} onClose={closeTask} />
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </TaskDetailsContext.Provider>
  );
};

export const useTaskDetails = (): TaskDetailsContextValue => {
  const ctx = useContext(TaskDetailsContext);
  if (!ctx) {
    throw new Error('useTaskDetails must be used within a TaskDetailsProvider');
  }
  return ctx;
};

export default TaskDetailsProvider;
